import { useState } from "react";
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import NotificationGroupEditor from "./NotificationGroupEditor.tsx";
import SaveProblem from "./SaveProblem.tsx";
import { useAlertSettings, useSaveAlertSettings } from "@/hooks/useAlertSettings.ts";
import {
  DELETE_GROUP_CONFIRM,
  lastChanged,
  NO_GROUPS,
  notificationGroupsTitle,
  recipientCount,
  NOTIFICATION_RULE_LABELS,
} from "@/utils/alertCopy.ts";
import { loadErrorMessage, rejections, toGroupInput } from "@/utils/notificationGroups.ts";
import type { AlertSettings, AlertSettingsUpdate, NotificationGroup, NotificationGroupInput } from "@/types";

interface RowProps {
  readonly group: NotificationGroup;
  readonly busy: boolean;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

function GroupRow({ group, busy, onEdit, onDelete }: RowProps) {
  const [confirming, setConfirming] = useState(false);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
          {group.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {recipientCount(group.emails.length)}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {group.rules.map((rule) => (
            <Chip key={rule} size="small" label={NOTIFICATION_RULE_LABELS[rule] ?? rule} />
          ))}
        </Box>
      </Box>
      {confirming ? (
        <>
          <Typography variant="body2">{DELETE_GROUP_CONFIRM}</Typography>
          <Button color="error" disabled={busy} onClick={onDelete}>
            Delete
          </Button>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
        </>
      ) : (
        <>
          <Button disabled={busy} onClick={onEdit}>
            Edit
          </Button>
          <Button color="error" disabled={busy} onClick={() => setConfirming(true)}>
            Delete
          </Button>
        </>
      )}
    </Box>
  );
}

interface BodyProps {
  readonly data: AlertSettings | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly busy: boolean;
  readonly onEdit: (index: number) => void;
  readonly onDelete: (index: number) => void;
}

function GroupsBody({ data, isLoading, error, busy, onEdit, onDelete }: BodyProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!data) return <MuiAlert severity="error">{loadErrorMessage(error)}</MuiAlert>;
  if (data.groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {NO_GROUPS}
      </Typography>
    );
  }
  return data.groups.map((group, index) => (
    <GroupRow key={group.id} group={group} busy={busy} onEdit={() => onEdit(index)} onDelete={() => onDelete(index)} />
  ));
}

function PanelActions({
  data,
  busy,
  saving,
  onAdd,
  onClose,
}: {
  readonly data: AlertSettings | undefined;
  readonly busy: boolean;
  readonly saving: boolean;
  readonly onAdd: () => void;
  readonly onClose: () => void;
}) {
  const changed = data?.updated_by && data.updated_at !== null ? lastChanged(data.updated_by, data.updated_at) : "";
  return (
    <DialogActions sx={{ justifyContent: "space-between", gap: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
        {changed}
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>
        {data && (
          <Button disabled={busy} onClick={onAdd}>
            Add group
          </Button>
        )}
        <Button disabled={saving} onClick={onClose}>
          Close
        </Button>
      </Box>
    </DialogActions>
  );
}

function withGroupAt(base: AlertSettings, index: number, group: NotificationGroupInput): AlertSettingsUpdate {
  const inputs = base.groups.map(toGroupInput);
  return { groups: [...inputs.slice(0, index), group, ...inputs.slice(index + 1)], version: base.version };
}

function withoutGroupAt(base: AlertSettings, index: number): AlertSettingsUpdate {
  return { groups: base.groups.filter((_, i) => i !== index).map(toGroupInput), version: base.version };
}

interface PanelProps {
  readonly company: string;
  readonly onClose: () => void;
}

/** The list as it was when the editor opened: the edit is saved into it, at its version. */
interface Draft {
  readonly index: number;
  readonly base: AlertSettings;
}

/** The viewed company's groups. Saves send the whole list with the version it was loaded at. */
export default function NotificationGroupsDialog({ company, onClose }: PanelProps) {
  const settings = useAlertSettings();
  const saving = useSaveAlertSettings();
  const [draft, setDraft] = useState<Draft | null>(null);
  const data = settings.data;
  // Until a refetch lands, the rows may show a list that has already changed.
  const busy = saving.isPending || settings.isFetching;

  const openEditor = (index: number | null) => {
    saving.reset();
    setDraft(index === null || !data ? null : { index, base: data });
  };
  const saveEdited = (group: NotificationGroupInput) => {
    if (draft) saving.save(withGroupAt(draft.base, draft.index, group), () => setDraft(null));
  };
  const deleteGroup = (index: number) => {
    if (data) saving.save(withoutGroupAt(data, index));
  };
  const reload = () => {
    openEditor(null);
    void settings.refetch();
  };

  return (
    <>
      <Dialog open onClose={saving.isPending ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle>{notificationGroupsTitle(company)}</DialogTitle>
        <DialogContent dividers>
          {draft === null && (
            <SaveProblem error={saving.error} other={rejections(saving.error).map((r) => r.msg)} onReload={reload} />
          )}
          <GroupsBody
            data={data}
            isLoading={settings.isLoading}
            error={settings.error}
            busy={busy}
            onEdit={openEditor}
            onDelete={deleteGroup}
          />
        </DialogContent>
        <PanelActions
          data={data}
          busy={busy}
          saving={saving.isPending}
          onAdd={() => openEditor(data?.groups.length ?? 0)}
          onClose={onClose}
        />
      </Dialog>
      {draft && (
        <NotificationGroupEditor
          group={draft.base.groups[draft.index] ?? null}
          groupIndex={draft.index}
          saving={saving.isPending}
          error={saving.error}
          onSave={saveEdited}
          onCancel={() => openEditor(null)}
          onReload={reload}
        />
      )}
    </>
  );
}
